import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTheme } from '@mui/material/styles';
import useTourPersistence, { readActiveGuide, writeActiveGuide } from './useTourPersistence';
import buildTourStyles, { tourOverlayColor } from './tourStyles';
import GuideBanner from './GuideBanner';
import { getToursForPath, getTourById, GUIDES, getGuideById, isStageOnPath } from './tours';

const TourContext = createContext(null);

const STYLE_TAG_ID = 'em-tour-styles';
// The sheet mounts asynchronously; give it a moment to settle before measuring.
const AUTO_START_DELAY = 1200;

// How long a text field has to hold the same value before an `advanceWhen:
// 'filled'` step treats it as done and moves on.
const FILLED_SETTLE_MS = 1500;

function selectorFor(step) {
  return `[data-tour="${step.target}"]`;
}

function describe(step) {
  if (!step.tip) return step.body;
  return `${step.body}<span class="em-tour-tip">${step.tip}</span>`;
}

export function TourProvider({ children }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { hasSeen, markStarted, markStep, markDismissed, markCompleted, resetAll } =
    useTourPersistence();

  const driverRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const deferredWatchRef = useRef(null);
  const exitListenerRef = useRef(null);
  const handoverRef = useRef(null);
  const autoAdvanceRef = useRef(null);
  // Tracks the last value seen in a field being filled, and when it appeared.
  const filledSinceRef = useRef(null);
  const [activeTourId, setActiveTourId] = useState(null);
  const [unavailableTourId, setUnavailableTourId] = useState(null);

  // Keep the themed stylesheet in sync with light/dark mode.
  useEffect(() => {
    let tag = document.getElementById(STYLE_TAG_ID);
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_TAG_ID;
      document.head.appendChild(tag);
    }
    tag.textContent = buildTourStyles(theme);
  }, [theme]);

  const stop = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    setActiveTourId(null);
  }, []);

  // Builds a driver.js instance for a list of steps. Shared by passive tours
  // and by each stage of an interactive guide.
  const buildDriver = useCallback(
    (
      steps,
      { onStep, onFinish, onExit, onOutsideClick, doneText, hideNav, allowTyping, isLastStage }
    ) =>
      driver({
        showProgress: steps.length > 1,
        showButtons: hideNav ? ['close'] : ['next', 'previous', 'close'],
        // Clicks on the dimmed area do nothing: that is where accidental
        // exits came from. Leaving is deliberate -- the Exit guide button, the
        // close icon, or clicking a real control elsewhere on the page.
        overlayClickBehavior: () => {},
        progressText: '{{current}} of {{total}}',
        allowClose: true,
        // Arrow keys step the tour and Escape closes it, which hijacks typing
        // when the highlighted area contains a form field.
        allowKeyboardControl: !allowTyping,
        // driver.js applies these inline on the overlay path; the alpha is baked
        // into the colour so opacity stays at 1.
        overlayColor: tourOverlayColor(theme),
        overlayOpacity: 1,
        stagePadding: 6,
        popoverOffset: 14,
        stageRadius: 8,
        popoverClass: 'em-tour-popover',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: doneText || 'Done',
        steps: steps.map((step, i) => {
          // A guide stage's last step points at something the user must
          // actually do -- Create, Save, Add New -- so it offers no Next.
          // Passive tours pass no isLastStage and keep theirs throughout.
          return {
            element: selectorFor(step),
            popover: {
              title: step.title,
              description: describe(step),
              side: step.placement || 'bottom',
              align: 'start',
            },
          };
        }),
        onCloseClick: () => {
          if (onExit) onExit();
          else driverRef.current?.destroy();
        },
        onPopoverRender: (popover, options) => {
          // Label the close control rather than leaving a bare x, which is
          // easy to miss now that clicking the dimmed area does nothing.
          popover.closeButton.classList.add('em-tour-exit');
          popover.closeButton.textContent = 'Exit Guide';
          popover.closeButton.setAttribute('aria-label', 'Exit guide');

          // A guide stage's last step points at something the user must
          // actually do -- Create, Save, Add New -- so it offers no Next.
          // driver.js never hides that button, it just relabels it, so hide
          // it here. Passive tours pass no isLastStage and keep theirs.
          if (isLastStage === undefined) return;
          const index = options?.state?.activeIndex ?? 0;
          // No Next on the last step, nor on any step that advances itself
          // once the user has done what it describes -- unless it offers both
          // routes, acting or Next, as a branch does.
          const step = steps[index];
          const branches = Boolean(step?.nextTo);
          const hideNext = index === steps.length - 1 || (Boolean(step?.advanceWhen) && !branches);
          popover.nextButton.style.display = hideNext ? 'none' : '';

          // Back is dead on the first step, and on any step reached by the
          // user acting rather than by clicking Next. A branching step leads
          // here by Next too, so it keeps Back alive.
          //
          // A step tied to a state has no use for Back either: the state is
          // still in force, so stepping back would be undone immediately.
          // Undoing the state is the way back, and does it for real.
          const previous = steps[index - 1];
          const hideBack =
            index === 0 ||
            Boolean(step?.holdsWhile) ||
            (Boolean(previous?.advanceWhen) && !previous?.nextTo);
          popover.previousButton.style.display = hideBack ? 'none' : '';
        },
        onHighlightStarted: (element, step, options) => {
          const index = options?.state?.activeIndex ?? 0;
          if (onStep) onStep(index);
        },
        onNextClick: (element, step, options) => {
          // A deferred step's element may still not exist -- its section was
          // never opened, or there was nothing to save. Step over any such
          // steps rather than highlighting nothing.
          const index = options?.state?.activeIndex ?? 0;
          let next = index + 1;
          // A branching step offers Next as the route past what it branches
          // into, so Next skips ahead to the step it names.
          const skipTo = steps[index]?.nextTo;
          if (skipTo) {
            const found = steps.findIndex((s) => s.target === skipTo);
            if (found > -1) next = found;
          }
          while (steps[next]?.deferred && !document.querySelector(selectorFor(steps[next]))) {
            next += 1;
          }
          if (next >= steps.length) {
            options.driver.destroy();
            return;
          }
          options.driver.moveTo(next);
        },
        onDestroyed: () => {
          if (onFinish) onFinish();
        },
      }),
    [theme]
  );

  // Steps whose target is actually on the page. A non-optional step that goes
  // missing means a `data-tour` attribute was lost -- say so in development.
  const availableSteps = useCallback(
    (owner, steps) =>
      steps.filter((step) => {
        if (step.deferred) return true;
        const found = document.querySelector(selectorFor(step));
        if (!found && !step.optional && import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn(`[tour:${owner}] missing required target "${step.target}"`);
        }
        return Boolean(found);
      }),
    []
  );

  const start = useCallback(
    (tourId) => {
      const tour = getTourById(tourId);
      if (!tour) return;

      const steps = availableSteps(tour.id, tour.steps);

      // Every target is missing -- usually because the tour was started from a
      // page that doesn't have them. Say so rather than appearing to do nothing.
      if (!steps.length) {
        setUnavailableTourId(tour.id);
        return;
      }

      if (driverRef.current) driverRef.current.destroy();

      const hasInput = steps.some((step) => {
        const el = document.querySelector(selectorFor(step));
        return el && (el.matches('input, textarea') || el.querySelector('input, textarea'));
      });

      const instance = buildDriver(steps, {
        allowTyping: hasInput,
        onStep: (index) => markStep(tour, index),
        onFinish: () => {
          // Reaching the end counts as completed; closing early counts as
          // dismissed. Either way the user has decided, so don't re-offer it.
          if (instance.hasNextStep()) markDismissed(tour);
          else markCompleted(tour);
          driverRef.current = null;
          setActiveTourId(null);
        },
      });

      driverRef.current = instance;
      markStarted(tour);
      setActiveTourId(tour.id);
      instance.drive();
    },
    [availableSteps, buildDriver, markStarted, markStep, markDismissed, markCompleted]
  );

  // End any running tour when the user navigates away.
  useEffect(() => {
    stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // --------------------------------------------------------------------
  // Interactive guides
  //
  // A guide is a list of stages, each tied to a page. Its position is kept in
  // localStorage so it survives the full page load that happens when the user
  // navigates. On every route change we look at the stored guide, work out
  // which stage this page is, and either run it or wait.
  // --------------------------------------------------------------------

  const [activeGuide, setActiveGuide] = useState(() => readActiveGuide());

  const stopGuide = useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (deferredWatchRef.current) {
      window.clearInterval(deferredWatchRef.current);
      deferredWatchRef.current = null;
    }
    if (exitListenerRef.current) {
      document.removeEventListener('click', exitListenerRef.current, true);
      exitListenerRef.current = null;
    }
    if (handoverRef.current) {
      window.clearInterval(handoverRef.current);
      handoverRef.current = null;
    }
    if (autoAdvanceRef.current) {
      window.clearInterval(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    filledSinceRef.current = null;
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    writeActiveGuide(null);
    setActiveGuide(null);
  }, []);

  const startGuide = useCallback((guideId) => {
    const guide = getGuideById(guideId);
    if (!guide) return;
    const next = { id: guide.id, stage: 0 };
    writeActiveGuide(next);
    setActiveGuide(next);
  }, []);

  // Advance to the next stage, or finish if that was the last one.
  const advanceGuide = useCallback((guide, stageIndex) => {
    if (stageIndex >= guide.stages.length - 1) {
      writeActiveGuide(null);
      setActiveGuide(null);
      return;
    }
    const next = { id: guide.id, stage: stageIndex + 1 };
    writeActiveGuide(next);
    setActiveGuide(next);
  }, []);

  const guide = activeGuide ? getGuideById(activeGuide.id) : null;
  const guideStageIndex = activeGuide?.stage ?? 0;
  const guideStage = guide?.stages?.[guideStageIndex] || null;

  // Is the user on the page this stage belongs to?
  const isOnGuidePage = useMemo(() => {
    if (!guide || !guideStage) return false;
    return isStageOnPath(guide, guideStageIndex, pathname);
  }, [guide, guideStage, guideStageIndex, pathname]);

  // The user may jump straight to a later stage's page, skipping the stage the
  // guide is waiting on. Follow them rather than stranding the guide behind.
  useEffect(() => {
    if (!guide || isOnGuidePage) return;
    const match = guide.stages.findIndex((_, i) => isStageOnPath(guide, i, pathname));
    if (match > guideStageIndex) {
      const next = { id: guide.id, stage: match };
      writeActiveGuide(next);
      setActiveGuide(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide, guideStageIndex, isOnGuidePage, pathname]);

  // Run the current stage once its page -- and its anchors -- are present.
  useEffect(() => {
    if (!guide || !guideStage || !isOnGuidePage) return undefined;

    let cancelled = false;

    // The dialog stages depend on an element that only appears after a click,
    // so poll briefly rather than giving up on the first miss.
    const attempt = (remaining) => {
      if (cancelled) return;

      const gateMet =
        !guideStage.requires || document.querySelector(`[data-tour="${guideStage.requires}"]`);
      const steps = gateMet ? availableSteps(guide.id, guideStage.steps) : [];

      if (!steps.length) {
        if (remaining > 0) {
          window.setTimeout(() => attempt(remaining - 1), 400);
        }
        return;
      }

      if (driverRef.current) driverRef.current.destroy();

      const hasInput = steps.some((step) => {
        const el = document.querySelector(selectorFor(step));
        return el && (el.matches('input, textarea') || el.querySelector('input, textarea'));
      });

      // Set when the handover watcher tears this stage down itself.
      let handedOver = false;

      const instance = buildDriver(steps, {
        allowTyping: hasInput,
        // The last step of a stage has no Next: progress comes from doing the
        // thing it points at. The final stage's last step ends the guide.
        isLastStage: guideStageIndex >= guide.stages.length - 1,
        // A one-step stage has nothing to page through at all.
        hideNav: steps.length === 1,
        onExit: () => stopGuide(),
        onFinish: () => {
          if (handedOver) return;
          driverRef.current = null;
          // A stage whose next stage is on the same route has no navigation to
          // advance on, so acting on its last step is the signal to move on.
          if (guideStage.advanceOnAction) {
            advanceGuide(guide, guideStageIndex);
            return;
          }
          // On a one-step stage the only button is close, so treat that as
          // opting out -- the real way forward is the navigation itself, which
          // the route-sync effect above picks up.
          if (steps.length === 1 || instance.hasNextStep()) stopGuide();
          else advanceGuide(guide, guideStageIndex);
        },
      });

      driverRef.current = instance;
      instance.drive();

      // Conditions a step can be tied to. `advanceWhen` moves on once one
      // holds; `holdsWhile` goes back once one stops holding. Both read the
      // same conditions, so a pair of steps describing the two states of one
      // control stays in agreement with it however the user gets there.
      const holds = (step, rule) => {
        if (!rule) return false;
        const el = document.querySelector(selectorFor(step));
        if (!el) return false;
        if (rule === 'filled') {
          const field = el.matches('input, textarea') ? el : el.querySelector('input, textarea');
          const value = field ? field.value.trim() : '';
          if (!value) {
            filledSinceRef.current = null;
            return false;
          }
          // Having typed one character is not the same as being done. Advancing
          // mid-word moves the popover over the very field being filled in.
          // Leaving the field says so outright; otherwise wait for a pause in
          // typing, tracked by watching the value settle.
          if (field !== document.activeElement) return true;
          const seen = filledSinceRef.current;
          if (!seen || seen.value !== value) {
            filledSinceRef.current = { value, at: Date.now() };
            return false;
          }
          return Date.now() - seen.at >= FILLED_SETTLE_MS;
        }
        if (rule === 'selected')
          return Boolean(el.querySelector('.Mui-selected, [aria-selected="true"]'));
        if (rule === 'expanded')
          return Boolean(document.querySelector('.MuiAccordion-root.Mui-expanded'));
        return false;
      };

      // Steps with an `advanceWhen` rule move on by themselves once the user
      // has done the thing they describe, so they need no Next button.
      const satisfied = (step) => holds(step, step?.advanceWhen);

      // A step tied to a state the user can undo goes back when they undo it:
      // the step and what is on screen should never disagree. Returning is as
      // deliberate as arriving -- both follow a click on the highlighted
      // control -- so this keeps the rule that the guide moves only when the
      // user acts, rather than making an exception to it.
      const lapsed = (step) => Boolean(step?.holdsWhile) && !holds(step, step.holdsWhile);

      const autoAdvance = window.setInterval(() => {
        if (driverRef.current !== instance) {
          window.clearInterval(autoAdvance);
          return;
        }
        // The stage lives inside a dialog; closing it (Cancel, Create, or the
        // backdrop) ends the guide rather than leaving an orphaned popover.
        if (guideStage.endsWithDialog && !document.querySelector('.MuiDialog-root')) {
          window.clearInterval(autoAdvance);
          stopGuide();
          return;
        }
        const index = instance.getActiveIndex?.() ?? 0;
        const current = steps[index];
        // Going back takes precedence: a step whose state has lapsed is
        // describing something no longer on screen, so it should not be the
        // one deciding whether to move on.
        if (lapsed(current) && index > 0) {
          filledSinceRef.current = null;
          instance.moveTo(index - 1);
          return;
        }
        if (!satisfied(current)) return;
        if (index + 1 < steps.length) {
          filledSinceRef.current = null;
          instance.moveTo(index + 1);
        }
      }, 400);
      autoAdvanceRef.current = autoAdvance;

      // A stage that hands over in place (a dialog opening rather than a page
      // change) has no navigation to advance on. Watch for the following
      // stage's gate to appear and move on when it does.
      if (guideStage.advanceOnAction) {
        const nextStage = guide.stages[guideStageIndex + 1];
        const gate = nextStage?.requires;
        if (gate) {
          const handover = window.setInterval(() => {
            if (driverRef.current !== instance) {
              window.clearInterval(handover);
              return;
            }
            if (document.querySelector(`[data-tour="${gate}"]`)) {
              window.clearInterval(handover);
              // Clear the ref first so onFinish knows this teardown is ours
              // and does not advance a second time.
              driverRef.current = null;
              handedOver = true;
              instance.destroy();
              advanceGuide(guide, guideStageIndex);
            }
          }, 300);
          handoverRef.current = handover;
        }
      }

      const onDocumentClick = (event) => {
        if (driverRef.current !== instance) return;
        const el = event.target;
        if (!el || typeof el.closest !== 'function') return;

        // An element already removed from the page -- a menu option that was
        // detached as the menu closed. It cannot be checked, so assume it
        // belonged to the guided action rather than ending the guide.
        if (!el.isConnected) return;

        // The dimmed overlay itself: ignore, this is the accidental click.
        if (el.closest('.driver-overlay') || el.tagName === 'svg' || el.tagName === 'path') {
          return;
        }

        // Inside the guide, or inside anything it opened.
        if (el.closest('.driver-popover, .driver-active-element')) return;

        // Portals (dropdown menus, dialogs, the unsaved-changes bar) render
        // outside the highlighted element, so a click in one looks like a
        // click elsewhere on the page. They are part of the guided action.
        if (
          el.closest(
            '.MuiAutocomplete-popper, .MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiSnackbar-root, .MuiBackdrop-root, [role="presentation"], [role="listbox"], [role="option"], [role="menu"], [role="menuitem"]'
          )
        ) {
          return;
        }

        // A menu that is mid-close has already detached its option from the
        // DOM by the time this runs, so the checks above cannot see it.
        // Treat any open popup as "still inside the guided action".
        if (
          document.querySelector(
            '.MuiAutocomplete-popper, .MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiSnackbar-root'
          )
        ) {
          return;
        }

        stopGuide();
      };
      document.addEventListener('click', onDocumentClick, true);
      exitListenerRef.current = onDocumentClick;

      // A deferred step's element goes away when the user acts on it -- both
      // Save and Cancel dismiss the unsaved-changes bar. Either way the step
      // is done, so close the guide rather than leaving an orphaned popover.
      const watch = window.setInterval(() => {
        if (driverRef.current !== instance) {
          window.clearInterval(watch);
          return;
        }
        const current = steps[instance.getActiveIndex?.() ?? 0];
        if (current?.deferred && !document.querySelector(selectorFor(current))) {
          window.clearInterval(watch);
          stopGuide();
        }
      }, 300);
      deferredWatchRef.current = watch;

      // Keep the cutout in step with an element that resizes mid-step.
      //
      // Only worth doing for real growth, such as a panel expanding. Small
      // shifts -- a field switching out of its error state, a helper line
      // appearing -- would otherwise re-render the popover mid-keystroke, and
      // driver.js recomputes placement on every render: in a tight dialog the
      // configured side stops fitting and the popover lands on the field the
      // user is typing into.
      if (typeof ResizeObserver !== 'undefined') {
        const active = document.querySelector('.driver-active-element');
        if (active) {
          const RESIZE_THRESHOLD = 12;
          let last = active.getBoundingClientRect();
          const observer = new ResizeObserver(() => {
            if (driverRef.current !== instance) return;
            // Never reposition out from under someone mid-keystroke. Only text
            // entry counts: clicking a panel shut also focuses the control
            // that closed it, and that resize does need following.
            const focused = document.activeElement;
            const typing =
              focused &&
              active.contains(focused) &&
              focused.matches('input:not([type="checkbox"]):not([type="radio"]), textarea');
            if (typing) return;
            const next = active.getBoundingClientRect();
            const grew =
              Math.abs(next.width - last.width) > RESIZE_THRESHOLD ||
              Math.abs(next.height - last.height) > RESIZE_THRESHOLD;
            if (!grew) return;
            last = next;
            instance.refresh();
          });
          observer.observe(active);
          resizeObserverRef.current = observer;
        }
      }
    };

    const timer = window.setTimeout(() => attempt(12), AUTO_START_DELAY);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (deferredWatchRef.current) {
        window.clearInterval(deferredWatchRef.current);
        deferredWatchRef.current = null;
      }
      if (exitListenerRef.current) {
        document.removeEventListener('click', exitListenerRef.current, true);
        exitListenerRef.current = null;
      }
      if (handoverRef.current) {
        window.clearInterval(handoverRef.current);
        handoverRef.current = null;
      }
      if (autoAdvanceRef.current) {
        window.clearInterval(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide, guideStageIndex, isOnGuidePage, pathname]);

  const availableTours = useMemo(() => getToursForPath(pathname), [pathname]);

  // Auto-start the first unseen tour for this page, once the page has settled.
  useEffect(() => {
    const candidate = availableTours.find((tour) => tour.autoStart && !hasSeen(tour));
    if (!candidate) return undefined;
    if (window.innerWidth < 900) return undefined;

    const timer = setTimeout(() => {
      // Don't interrupt a dialog the user already has open.
      if (document.querySelector('.MuiDialog-root, .MuiModal-root')) return;
      start(candidate.id);
    }, AUTO_START_DELAY);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, availableTours]);

  const hasUnseen = useMemo(
    () => availableTours.some((tour) => !hasSeen(tour)),
    [availableTours, hasSeen]
  );

  const value = useMemo(
    () => ({
      start,
      stop,
      activeTourId,
      availableTours,
      hasSeen,
      hasUnseen,
      resetAll,
      unavailableTourId,
      clearUnavailable: () => setUnavailableTourId(null),
      // Interactive guides are offered on every page, not just matching ones.
      guides: GUIDES,
      startGuide,
      stopGuide,
      activeGuide: guide
        ? {
            id: guide.id,
            title: guide.title,
            stage: guideStageIndex,
            stageCount: guide.stages.length,
            isOnGuidePage,
            waitingHint: guideStage?.waitingHint,
            waitingAnchors: guideStage?.waitingAnchors,
          }
        : null,
    }),
    [
      start,
      stop,
      activeTourId,
      availableTours,
      hasSeen,
      hasUnseen,
      resetAll,
      unavailableTourId,
      startGuide,
      stopGuide,
      guide,
      guideStage,
      guideStageIndex,
      isOnGuidePage,
    ]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <GuideBanner />
    </TourContext.Provider>
  );
}

TourProvider.propTypes = { children: PropTypes.node };

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside a TourProvider');
  return ctx;
}
